import "./reports.css";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Board,
  DateRangeProps,
  hasPermission,
  OperationArea,
  Panel,
  SegmentedControl,
  SegmentedControlItemProps,
  StringMap,
} from "../../components";
import { useApiFetch, useAuth, useLanguage } from "../../hooks";
import {
  EMPTY_SALES_FILTER_OPTIONS,
  OWN_SALES_PRODUCT_FILTER,
  SALES_CATEGORY_FILTER,
  SALES_FI_TERMINAL_SELECT_FILTER,
  salesQueryParams,
  productOptionLabel,
  ReportSalesFilterOptionsProps,
  ReportsTabEnum,
  ReportTabRow,
} from "./reports.props";
import {
  ReportFilters,
  ReportOwnSales,
  ReportSalesMyProducts,
  ReportStatusCards,
  ReportUnknown,
} from "./components";
import {
  buildQueryString,
  getCurrentMonthToTodayRange,
  resetObjectValues,
  toISODateFromDMY,
} from "../../utils";
import { API_URL, downloadFile } from "../../services";

const ReportsScreen: React.FC = () => {
  const { t } = useLanguage();
  const { rol } = useAuth();
  const location = useLocation();

  const tableId: string = "table-reports";
  const tabs: SegmentedControlItemProps[] = [
    // Informe de marca: solo tarjetas de productos emitidos por Area01.
    {
      id: ReportsTabEnum.ReportsStatusCard,
      text: t.reportsScreen.statusCards,
      withPermissions: ["brand"],
    },
    {
      id: ReportsTabEnum.ReportsOwnSales,
      text: t.reportsScreen.ownSales,
      withPermissions: ["channel"],
    },
    {
      id: ReportsTabEnum.ReportsSalesMyProducts,
      text: t.reportsScreen.salesMyProducts,
      withPermissions: ["brand"],
    },
  ];

  // Pestaña por defecto: la primera a la que el usuario tiene acceso (un canal
  // no ve "Estado tarjetas").
  const allowedTabs = tabs.filter(
    (tab) => !tab.withPermissions || hasPermission(rol, tab.withPermissions)
  );
  const defaultTab = allowedTabs[0] ?? tabs[0];

  const [tab, setTab] = useState<SegmentedControlItemProps>(defaultTab);
  const [tabInitial, setTabInitial] = useState<SegmentedControlItemProps>(tab);
  const [dateRange, setDateRange] = useState<DateRangeProps>({
    startDate: "01/10/2025",
    endDate: "02/10/2025",
  });
  const [search, setSearch] = useState<string>("");
  const [preFilters, setPreFilters] = useState<StringMap>({});
  const [filters, setFilters] = useState<StringMap>({});
  const [exportDisabled, setExportDisabled] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<boolean>(false);
  const [errorFetchData, setErrorFetchData] = useState<boolean>(false);
  const [fetchSalesOptions, salesOptions] =
    useApiFetch<ReportSalesFilterOptionsProps>(EMPTY_SALES_FILTER_OPTIONS);
  // Filas cargadas en la pestaña activa, para las opciones de los filtros.
  const [tabRows, setTabRows] = useState<ReportTabRow[]>([]);

  const deleteByKey = (key: string) => {
    setFilters({ ...filters, [key]: "" });
    setPreFilters({ ...preFilters, [key]: "" });
  };

  const handleCleanFilters = () => {
    setErrorFetchData(false);
    setFilters(resetObjectValues(filters));
    setPreFilters(resetObjectValues(preFilters));
    setSearch("");
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(false);

    const isSalesTab = tab.id !== ReportsTabEnum.ReportsStatusCard;
    const query = buildQueryString(
      isSalesTab
        ? salesQueryParams(filters, dateRange, search)
        : {
            expiresBefore: toISODateFromDMY(filters.expiresBefore ?? ""),
            dateFrom: toISODateFromDMY(dateRange.startDate),
            dateTo: toISODateFromDMY(dateRange.endDate),
          }
    );

    const endpoint =
      tab.id === ReportsTabEnum.ReportsOwnSales
        ? API_URL.OWN_SALES(`/export${query}`)
        : tab.id === ReportsTabEnum.ReportsSalesMyProducts
        ? API_URL.SALES_MY_PRODUCTS(`/export${query}`)
        : API_URL.CARDS(`/export${query}`);

    const filename = `${tab.text.length ? tab.text : "tabla_reportes"}${
      dateRange
        ? ` ${dateRange.startDate.replaceAll(
            "/",
            "-"
          )} - ${dateRange.endDate.replaceAll("/", "-")}`
        : ""
    }.xlsx`;

    const ok = await downloadFile(endpoint, filename);
    setExporting(false);
    if (!ok) {
      setExportError(true);
    }
  };

  useEffect(() => {
    const tabss: SegmentedControlItemProps[] = [];
  }, []);

  useEffect(() => {
    setDateRange(getCurrentMonthToTodayRange());
  }, []);

  useEffect(() => handleCleanFilters(), [tab]);

  // Opciones de marca y producto de los informes de ventas.
  useEffect(() => {
    setTabRows([]);
    if (tab.id === ReportsTabEnum.ReportsOwnSales) {
      fetchSalesOptions(API_URL.OWN_SALES("/filter-options"), {
        method: "GET",
      });
    } else if (tab.id === ReportsTabEnum.ReportsSalesMyProducts) {
      fetchSalesOptions(API_URL.SALES_MY_PRODUCTS("/filter-options"), {
        method: "GET",
      });
    }
  }, [tab.id]);

  // Texto de los chips de filtro: marca y producto se guardan por id.
  const filterChipValue = (key: string, value: string): string => {
    if (key === "operation" || key === "status") {
      return (
        (t.reportsScreen.table.tbody as Record<string, string>)[value] ?? value
      );
    }
    if (key === OWN_SALES_PRODUCT_FILTER) {
      const product = salesOptions?.products.find(
        (option) => option.idProd === value
      );
      return product ? productOptionLabel(product) : value;
    }
    return value;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabValue = queryParams.get("tab");

    if (tabValue !== undefined || tabValue !== null) {
      setTabInitial(allowedTabs.find((t) => t.id === tabValue) ?? defaultTab);
    }
  }, [location.search, rol]);

  useEffect(() => setTab(tabInitial), [tabInitial]);

  return (
    <>
      <Panel>
        <section className="reports-header-wrapper">
          <h2>{t.reportsScreen.title}</h2>

          <SegmentedControl
            options={tabs}
            initial={tabInitial.id}
            onChange={(newTab) => setTab(tabs.find((t) => t.id === newTab)!)}
            className="segmented-spacer-top"
          />
        </section>

        {exportError && (
          <p className="reports-export-error">{t.reportsScreen.exportError}</p>
        )}

        <OperationArea
          title={tab.text}
          filter={{
            show: true,
            filters: filters,
            // Filtros cuyo id no coincide con una cabecera de columna.
            pathFilterL: {
              ...t.reportsScreen.table.thead,
              retailerSelect: t.reportsScreen.table.thead.retailer,
              [SALES_CATEGORY_FILTER]: t.reportsScreen.table.thead.category,
              [SALES_FI_TERMINAL_SELECT_FILTER]: t.reportsScreen.table.thead.fiTerminal,
              [OWN_SALES_PRODUCT_FILTER]: t.reportsScreen.table.thead.product,
              brandName: t.reportsScreen.table.thead.brand,
            },
            // Operación, estado, marca y producto se guardan como código o id:
            // se muestran con su nombre.
            formatValue: filterChipValue,
            deleteByKey,
            deleteAll: handleCleanFilters,
            modal: {
              content: (
                <ReportFilters
                  type={tab.id as ReportsTabEnum}
                  filters={preFilters}
                  setFilters={setPreFilters}
                  salesOptions={salesOptions}
                  rows={tabRows}
                />
              ),
              textButton: t.reportsScreen.filters.onOk,
              backdropClosable: false,
              onOk() {
                setFilters(preFilters);
              },
            },
          }}
          calendar={{
            show: true,
            dateRange: dateRange,
            setDateRange: setDateRange,
          }}
          exportdata={{
            show: true,
            onClick: handleExport,
            text: t.reportsScreen.exportExcel,
            disabled: exportDisabled || exporting,
          }}
          search={{
            show: true,
            value: search,
            setValue: setSearch,
            placeholder: t.components.operations.area.search,
          }}
        >
          <Board id={tableId} error={errorFetchData}>
            {tabs.map((t, i) => {
              // Hasta que se cargan los roles, ninguna pestaña está permitida: así
              // no se monta (ni pide datos) un informe al que el usuario no tiene acceso.
              const isAllowed = allowedTabs.some((allowed) => allowed.id === t.id);
              return (
                <React.Fragment key={`reports-contents-${t.id}-${i}`}>
                  {t.id === tab.id && isAllowed && (
                    <>
                      {t.id === ReportsTabEnum.ReportsStatusCard ? (
                        <ReportStatusCards
                          key={`reports-${t.id}-${i}`}
                          search={search}
                          filters={filters}
                          dateRange={dateRange}
                          setExportDisabled={setExportDisabled}
                          setErrorFetchData={setErrorFetchData}
                        />
                      ) : t.id === ReportsTabEnum.ReportsOwnSales ? (
                        <ReportOwnSales
                          key={`reports-${t.id}-${i}`}
                          search={search}
                          filters={filters}
                          dateRange={dateRange}
                          setExportDisabled={setExportDisabled}
                          setErrorFetchData={setErrorFetchData}
                        />
                      ) : t.id === ReportsTabEnum.ReportsSalesMyProducts ? (
                        <ReportSalesMyProducts
                          key={`reports-${t.id}-${i}`}
                          onRowsChange={setTabRows}
                          search={search}
                          filters={filters}
                          dateRange={dateRange}
                          setExportDisabled={setExportDisabled}
                          setErrorFetchData={setErrorFetchData}
                        />
                      ) : (
                        <ReportUnknown key={`reports-${t.id}-${i}-unknown`} />
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </Board>
        </OperationArea>
      </Panel>
    </>
  );
};

export default ReportsScreen;
